import React from "react";
import DirectoryEditDialog from "./DirectoryEditDialog";

import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";

import { directoryColumns } from "@/lib/columns";
import { Link } from "react-router";
import {
  Loader2,
  MoreHorizontal,
  PlusCircle,
  Trash,
  Trash2,
} from "lucide-react";
import { DirectoryType } from "@/lib/interfaces";
import { axiosPrivate } from "@/api/axios";
import { toast } from "sonner";
import { Row } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DropdownMenu } from "@/components/ui/dropdown-menu";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { Pencil } from "lucide-react";

function DirectoryPage() {
  const queryClient = useQueryClient();
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingEntry, setEditingEntry] = React.useState<DirectoryType | null>(null);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});

  const { data, isLoading } = useQuery({
    queryKey: ["directories"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/dxn-directory/");
      if (!res.data?.success) {
        toast.error("Failed to fetch directory entries. Please try again later.");
        throw new Error("Failed to fetch directory.");
      }
      return res.data.data || [];
    },
    placeholderData: [],
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosPrivate.delete(`/dxn-directory/${id}`);
      if (!res.data?.success) throw new Error("Failed to delete entry.");
      return id;
    },
    onMutate: async () => {
      toast.loading("Deleting entry...");
    },
    onSuccess: (id: number) => {
      queryClient.setQueryData<DirectoryType[]>(["directories"], (old) =>
        old ? old.filter((e) => e.id !== id) : []
      );
      toast.dismiss();
      toast.info("Entry deleted.");
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete entry. Please try again later.");
    },
  });

  function handleDelete(id: number) {
    deleteEntryMutation.mutate(id);
  }

  const actions = {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<DirectoryType> }) => {
      return (
        <>
          <ActionsMenu
            onEdit={() => {
              setEditingEntry(row.original as DirectoryType);
              setEditDialogOpen(true);
            }}
            onDelete={() => {
              confirmActionButtonRef.current = () => handleDelete(row.original.id as number);
              setOpenConfirmDialog(true);
            }}
          />
        </>
      );
    },
  };

  const columns = [...directoryColumns, actions];

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">DXN Office Directory</h1>
      <div className="flex items-center justify-between">
        <p className="text-lg">Manage all directory entries</p>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          placeholder="Filter Directory"
          filterColumns={["country","name","person","city"]}
        />
      )}
      <DirectoryEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        entry={editingEntry}
      />
      <ConfirmDialog
        actionButtonRef={confirmActionButtonRef}
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        isLoading={deleteEntryMutation.isPending}
      />
    </div>
  );
}

export default DirectoryPage;
